import { useEffect, useState } from "react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { fetchSettings, mediaUrl } from "../lib/api";

const STUDIO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/2981571f-77f9-42a9-85e2-d8389be9ce3e/images/07da495460665a99ca037554c1554cc623e47b122890182371b0098b09e2b384.png";

const TIMELINE = [
    { year: "2003", text: "Anita founded ASC Interior in Bangkok. Building credibility through early commissions, referral by referral." },
    { year: "2011", text: "First international hospitality project: Dream Hotel Bangkok. The work proved itself outside Thailand. Dream Hotel earned early recognition and awards." },
    { year: "2015", text: "Studio expanded into residences and procurement. Learned the value of focus over growth." },
    { year: "2019 — 2023", text: "Adapted during COVID by pivoting to turnkey projects and collaborations. Earned accolades as the industry shifted." },
    { year: "2024 — 2025", text: "From a three-person studio to 150+ projects across Asia and the Middle East. Recognised as Outstanding Leader in Asia. Restructured around core principles: listening deeply, delivering consistently, building genuine partnerships. Building spaces, building trust." },
];

const VALUES = [
    {
        t: "Atmosphere",
        d: "Spaces that breathe. We design rooms that feel calm, intuitive and genuinely livable—layering light, proportion, materiality and detail to create a lasting sense of place. The measure is how a space feels to inhabit, not how it reads in photographs.",
    },
    {
        t: "Hospitality",
        d: "We design for movement and meaning. Every project should support how guests arrive, gather, rest and move through space. We also design the invisible: staff flows, kitchen logistics, service access. Good hospitality isn't luxury—it's thoughtfulness made physical.",
    },
    {
        t: "Context",
        d: "We listen to place before we design. Each project responds to its location, architecture, history and cultural setting. Rather than impose a signature style, we shape spaces that feel discovered, not invented. Appropriate. Grounded. Enduring.",
    },
    {
        t: "Longevity",
        d: "We build for time, not openings. Materials should age naturally and improve with use. Operations should remain practical across decades. Spaces should deepen in relevance, not fade. Longevity comes from restraint, not expense.",
    },
];

export default function About() {
    const [studio, setStudio] = useState({ image: "", image_focal: null });
    useEffect(() => {
        fetchSettings()
            .then((s) => setStudio(s?.studio_section || {}))
            .catch(() => {});
    }, []);
    const heroSrc = mediaUrl(studio.image) || STUDIO_IMG;
    const heroFocal = studio.image_focal || null;
    return (
        <main data-testid="page-about" className="pt-32 md:pt-40">
            <Seo
                path="/about"
                title="Studio — Hospitality Interiors, Bangkok since 2003"
                description="ASC Interior Co., Ltd. — a Bangkok studio founded in 2003. Building spaces, building trust: hospitality interiors for hotels, resorts, restaurants and residences across Asia and the Middle East."
            />
            <section className="px-6 md:px-12 lg:px-24 pb-20">
                <Reveal>
                    <p className="asc-overline">The Studio · Founded 2003</p>
                </Reveal>
                <Reveal delay={120}>
                    <h1 className="serif text-5xl sm:text-6xl lg:text-[5.5rem] mt-6 leading-[1.02] tracking-tight font-light max-w-5xl">
                        Building Spaces.
                        <br />
                        Building <span className="italic">Trust.</span>
                    </h1>
                </Reveal>
                <Reveal delay={220}>
                    <p className="mt-12 max-w-2xl text-lg lg:text-xl text-[#5C5855] leading-relaxed">
                        We&apos;ve learned what matters: listening deeply, delivering
                        consistently, designing for real people—not just photographs.
                        Our practice has evolved. Our commitment hasn&apos;t. Hotels,
                        resorts, restaurants, residences. Spaces where thoughtful
                        design meets genuine hospitality.
                    </p>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                <div className="asc-img-zoom aspect-[16/9]">
                    <img
                        src={heroSrc}
                        alt="ASC studio detail"
                        className="w-full h-full object-cover"
                        style={heroFocal ? {
                            objectPosition: `${heroFocal.x ?? 50}% ${heroFocal.y ?? 50}%`,
                            transform: `scale(${heroFocal.zoom ?? 1})`,
                            transformOrigin: `${heroFocal.x ?? 50}% ${heroFocal.y ?? 50}%`,
                        } : undefined}
                    />
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="asc-overline">Our Philosophy</p>
                            <h2 className="serif text-4xl lg:text-5xl mt-6 leading-[1.1] tracking-tight">
                                Four <span className="italic">principles.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <div data-testid="values-grid" className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E1DB]">
                            {VALUES.map((v) => (
                                <div key={v.t} className="bg-[#F9F8F6] p-8 lg:p-10">
                                    <p className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#1A1A1A]">
                                        {v.t}
                                    </p>
                                    <p className="serif text-lg lg:text-xl mt-5 leading-[1.55] font-light text-[#1A1A1A]">
                                        {v.d}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-24 pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="asc-overline">Studio timeline</p>
                            <h2 className="serif text-4xl lg:text-5xl mt-6 leading-[1.1] tracking-tight">
                                A <span className="italic">slow</span> arc.
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <ul data-testid="timeline" className="border-t border-[#E5E1DB]">
                            {TIMELINE.map((t, i) => (
                                <Reveal key={i} delay={i * 60}>
                                    <li className="grid grid-cols-12 gap-4 py-7 border-b border-[#E5E1DB]">
                                        <span className="col-span-12 sm:col-span-3 serif text-2xl sm:text-3xl tracking-tight font-light text-[#1A1A1A] inline-flex items-start whitespace-nowrap">
                                            <span className="bg-[var(--asc-yellow)] px-3 py-1 inline-block">
                                                {t.year}
                                            </span>
                                        </span>
                                        <span className="col-span-12 sm:col-span-9 text-base lg:text-lg text-[#1A1A1A] leading-relaxed">
                                            {t.text}
                                        </span>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </main>
    );
}
